/**
 * WhatsApp Admin Monitor
 * Shows all leads with WhatsApp conversations. Admin can read and reply.
 * Only visible to admin / superadmin roles.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCRM } from '../context/CRMContext';
import {
  collection, query, onSnapshot, orderBy, where,
  getDocs, writeBatch, doc, updateDoc, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ─── Timestamp formatter ──────────────────────────────────────────────────────
function fmtTime(ts) {
  if (!ts) return '';
  const d = ts?.toDate?.() || (ts?.seconds ? new Date(ts.seconds * 1000) : null);
  if (!d) return '';
  const now = new Date();
  if ((now - d) < 86400000) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function fmtFull(ts) {
  if (!ts) return '';
  const d = ts?.toDate?.() || (ts?.seconds ? new Date(ts.seconds * 1000) : null);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

// ─── Chat thread for selected lead ───────────────────────────────────────────
function ChatThread({ lead, sessions, currentUser }) {
  const [messages, setMessages]   = useState([]);
  const [text,     setText]       = useState('');
  const [sending,  setSending]    = useState(false);
  const msgEndRef = useRef(null);

  useEffect(() => {
    if (!lead?.id) return;
    // Only filter by leadId — no orderBy needed, sort in JS to avoid composite index requirement
    const q = query(collection(db, 'waMessages'), where('leadId', '==', lead.id));
    return onSnapshot(
      q,
      snap => {
        const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        msgs.sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));
        setMessages(msgs);
      },
      () => setMessages([])
    );
  }, [lead?.id]);

  // Mark messages read and clear unread counter
  useEffect(() => {
    if (!lead?.id) return;
    const markRead = async () => {
      const q = query(collection(db, 'waMessages'), where('leadId', '==', lead.id), where('read', '==', false));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const batch = writeBatch(db);
        snap.docs.forEach(d => batch.update(d.ref, { read: true }));
        await batch.commit();
      }
      await updateDoc(doc(db, 'leads', lead.id), { waUnread: 0 });
    };
    markRead().catch(() => {});
  }, [lead?.id]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Determine which counselor session to use for sending
  const assignedCounselorId = lead.assignedToId || lead.waLastCounselorId;
  const activeSession = assignedCounselorId ? sessions.find(s => s.id === assignedCounselorId && s.status === 'connected') : null;

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || !activeSession) return;
    const phone = (lead.phone || '').replace(/\D/g, '');
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wa/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counselorId: activeSession.id, phone, text: trimmed, leadId: lead.id }),
      });
      if (!res.ok) { const d = await res.json(); alert(d.error || 'Failed to send'); return; }
      setText('');
    } catch (e) { alert('Send failed — is the backend running?'); }
    finally { setSending(false); }
  };

  const leadName = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.name || lead.phone || 'Unknown';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Thread header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #25d366, #128c7e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
            {(leadName[0] || '?').toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{leadName}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{lead.phone}</div>
          </div>
          {activeSession ? (
            <div style={{ marginLeft: 'auto', fontSize: 11, color: '#25d366', background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)', borderRadius: 20, padding: '3px 10px', fontWeight: 600 }}>
              Sending via +{activeSession.phone}
            </div>
          ) : (
            <div style={{ marginLeft: 'auto', fontSize: 11, color: '#f59e0b', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 20, padding: '3px 10px', fontWeight: 600 }}>
              ⚠ Assigned counselor not connected
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc' }}>
        {messages.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>No messages yet.</div>
        )}
        {messages.map(msg => {
          const isMe = msg.fromMe;
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              {!isMe && <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, paddingLeft: 4 }}>{leadName}</div>}
              <div style={{
                maxWidth: '72%', padding: '9px 13px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: isMe ? 'linear-gradient(135deg, #dcf8c6, #c5f0a4)' : '#fff',
                border: '1px solid', borderColor: isMe ? 'rgba(37,211,102,0.3)' : 'rgba(0,0,0,0.08)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontSize: 13, color: '#0f172a', lineHeight: 1.55, wordBreak: 'break-word' }}>{msg.body || '[Media]'}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, textAlign: 'right' }}>
                  {fmtFull(msg.timestamp)}{isMe && ' ✓'}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={msgEndRef} />
      </div>

      {/* Send box */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(0,0,0,0.06)', background: '#fff', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
        <textarea value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={activeSession ? `Reply as +${activeSession.phone}…` : 'Assign counselor must be connected first…'}
          disabled={!activeSession}
          rows={1}
          style={{ flex: 1, resize: 'none', padding: '9px 13px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.12)', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: activeSession ? '#fff' : '#f8fafc', color: activeSession ? '#0f172a' : '#94a3b8' }}
        />
        <button onClick={handleSend} disabled={!text.trim() || !activeSession || sending}
          style={{ width: 40, height: 40, borderRadius: 10, background: text.trim() && activeSession ? 'linear-gradient(135deg, #25d366, #128c7e)' : 'rgba(0,0,0,0.06)', border: 'none', color: text.trim() && activeSession ? '#fff' : '#94a3b8', cursor: text.trim() && activeSession ? 'pointer' : 'not-allowed', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {sending ? '…' : '➤'}
        </button>
      </div>
    </div>
  );
}

// ─── Main monitor page ─────────────────────────────────────────────────────────
export default function WhatsAppMonitor() {
  const { leads, users, currentUser, getUserById } = useCRM();
  const [sessions,      setSessions]      = useState([]);
  const [allMessages,   setAllMessages]   = useState([]);
  const [selectedLead,  setSelectedLead]  = useState(null);
  const [filterCounselor, setFilterCounselor] = useState('all');
  const [searchQ,       setSearchQ]       = useState('');

  // Check role
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // Subscribe to WA sessions for this company
  useEffect(() => {
    const companyId = currentUser?.companyId || 'default';
    const q = query(collection(db, 'waSessions'), where('companyId', '==', companyId));
    return onSnapshot(q,
      snap => setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setSessions([])
    );
  }, [currentUser?.companyId]);

  // Subscribe to recent messages for this company — no orderBy to avoid composite index
  useEffect(() => {
    const companyId = currentUser?.companyId || 'default';
    const q = query(collection(db, 'waMessages'), where('companyId', '==', companyId));
    return onSnapshot(
      q,
      snap => {
        const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort newest first in JS
        msgs.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
        setAllMessages(msgs);
      },
      () => setAllMessages([])
    );
  }, [currentUser?.companyId]);

  // Build conversation list: unique leadIds with latest message info
  const conversations = React.useMemo(() => {
    const map = {};
    for (const msg of allMessages) {
      if (!msg.leadId) continue;
      if (!map[msg.leadId]) {
        map[msg.leadId] = {
          leadId:       msg.leadId,
          counselorId:  msg.counselorId,
          lastMessage:  msg.body || '[Media]',
          lastTs:       msg.timestamp,
          unread:       0,
        };
      }
      if (!msg.read && !msg.fromMe) map[msg.leadId].unread++;
    }
    return Object.values(map).sort((a, b) => {
      const at = a.lastTs?.seconds || 0;
      const bt = b.lastTs?.seconds || 0;
      return bt - at;
    });
  }, [allMessages]);

  const filteredConvos = React.useMemo(() => {
    return conversations.filter(c => {
      const lead = leads.find(l => l.id === c.leadId);
      if (!lead) return false;
      const name = [lead.firstName, lead.lastName, lead.name, lead.phone].filter(Boolean).join(' ').toLowerCase();
      if (searchQ && !name.includes(searchQ.toLowerCase())) return false;
      if (filterCounselor !== 'all' && lead.assignedToId !== filterCounselor) return false;
      return true;
    });
  }, [conversations, leads, searchQ, filterCounselor]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>Admin access only</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Only admins can view the WhatsApp monitor.</div>
      </div>
    );
  }

  const selectedLeadObj = selectedLead ? leads.find(l => l.id === selectedLead) : null;

  return (
    <div style={{ display: 'flex', height: '100%', background: '#f1f5f9', overflow: 'hidden' }}>

      {/* ── LEFT: conversation list ── */}
      <div style={{ width: 320, background: '#fff', borderRight: '1px solid rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {/* Header */}
        <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>💬 WhatsApp Monitor</h2>
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search leads…"
            className="crm-input" style={{ width: '100%', marginBottom: 8 }} />
          <select value={filterCounselor} onChange={e => setFilterCounselor(e.target.value)} className="crm-select" style={{ width: '100%' }}>
            <option value="all">All counselors</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        {/* Session status strip */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {sessions.filter(s => s.status === 'connected').map(s => {
            const u = users.find(x => x.id === s.id);
            return (
              <span key={s.id} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(37,211,102,0.08)', color: '#128c7e', border: '1px solid rgba(37,211,102,0.2)', fontWeight: 600 }}>
                ● {u?.name || s.id.slice(0, 8)}
              </span>
            );
          })}
          {sessions.filter(s => s.status === 'connected').length === 0 && (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>No connected sessions</span>
          )}
        </div>

        {/* Conversation list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredConvos.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No conversations yet.
            </div>
          )}
          {filteredConvos.map(conv => {
            const lead = leads.find(l => l.id === conv.leadId);
            if (!lead) return null;
            const leadName = [lead.firstName, lead.lastName, lead.name].filter(Boolean).join(' ') || lead.phone || 'Unknown';
            const counselor = getUserById(lead.assignedToId);
            const isActive  = selectedLead === conv.leadId;
            return (
              <div key={conv.leadId} onClick={() => setSelectedLead(conv.leadId)}
                style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', cursor: 'pointer', background: isActive ? 'rgba(124,58,237,0.06)' : '#fff', transition: 'background 0.15s', borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, #25d366, #128c7e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#fff', fontWeight: 700, flexShrink: 0, position: 'relative' }}>
                    {(leadName[0] || '?').toUpperCase()}
                    {conv.unread > 0 && (
                      <span style={{ position: 'absolute', top: -3, right: -3, width: 16, height: 16, borderRadius: '50%', background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {conv.unread > 9 ? '9+' : conv.unread}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                      <div style={{ fontSize: 13, fontWeight: conv.unread > 0 ? 700 : 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{leadName}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>{fmtTime(conv.lastTs)}</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.lastMessage}</div>
                    {counselor && <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 2 }}>📋 {counselor.name}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: chat thread ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedLeadObj ? (
          <ChatThread lead={selectedLeadObj} sessions={sessions} currentUser={currentUser} />
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#94a3b8' }}>
            <div style={{ fontSize: 64 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Select a conversation</div>
            <div style={{ fontSize: 13 }}>Click a lead from the list on the left to view the chat.</div>
          </div>
        )}
      </div>
    </div>
  );
}
